import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Users, Award, Clock, User, Building, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-sm text-gray-600">
            <a href="/" className="text-blue-500 hover:underline">AP Pacific</a> &gt; {t('about.breadcrumb')}
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-bold text-gray-800">{t('about.pageTitle')}</h1>
        </div>
      </div>

      {/* About Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="flex justify-center lg:justify-start">
              <img 
                src="https://customer-assets.emergentagent.com/job_react-appacific/artifacts/lczc15jx_carpet_cleaning_technician.png"
                alt="Professional cleaning technician"
                className="rounded-lg shadow-lg max-w-full h-auto"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>

            {/* Right Side - Content */}
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">
                {t('about.welcomeTitle')}
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('about.paragraph1')}
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('about.paragraph2')}
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {t('about.paragraph3')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-green-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {t('about.features.staff.title')}
              </h3>
              <p className="text-gray-600">
                {t('about.features.staff.description')}
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {t('about.features.tools.title')}
              </h3>
              <p className="text-gray-600">
                {t('about.features.tools.description')}
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {t('about.features.service.title')}
              </h3>
              <p className="text-gray-600">
                {t('about.features.service.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-blue-500 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="relative mb-6">
                <User className="w-16 h-16 mx-auto mb-2" />
              </div>
              <div className="text-4xl font-bold mb-2">1000+</div>
              <div className="text-lg">{t('about.stats.customers')}</div>
            </div>
            <div className="text-center">
              <div className="relative mb-6">
                <Building className="w-16 h-16 mx-auto mb-2" />
              </div>
              <div className="text-4xl font-bold mb-2">500K+</div>
              <div className="text-lg">{t('about.stats.squareFeet')}</div>
            </div>
            <div className="text-center">
              <div className="relative mb-6">
                <Calendar className="w-16 h-16 mx-auto mb-2" />
              </div>
              <div className="text-4xl font-bold mb-2">1990</div>
              <div className="text-lg">{t('about.stats.since')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 text-center mb-12 uppercase">
            {t('about.testimonials.title')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <img
                  src="https://images.unsplash.com/photo-1494790108755-2616b612b786?w=64&h=64&fit=crop&crop=face"
                  alt={t('about.testimonials.customer1.name')}
                  className="w-16 h-16 rounded-full mr-4"
                />
                <div>
                  <h4 className="font-bold text-gray-800">{t('about.testimonials.customer1.name')}</h4>
                  <p className="text-gray-600 text-sm">{t('about.testimonials.customer1.role')}</p>
                </div>
              </div>
              <p className="text-gray-700 italic">"{t('about.testimonials.customer1.text')}"</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <img
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=face"
                  alt={t('about.testimonials.customer2.name')}
                  className="w-16 h-16 rounded-full mr-4"
                />
                <div>
                  <h4 className="font-bold text-gray-800">{t('about.testimonials.customer2.name')}</h4>
                  <p className="text-gray-600 text-sm">{t('about.testimonials.customer2.role')}</p>
                </div>
              </div>
              <p className="text-gray-700 italic">"{t('about.testimonials.customer2.text')}"</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;